/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { uploadImageURL } from '../../../../../Creation/Image/ImageUploadHelper';
import {
	validateFileTypeFromUrl,
	verifyImageTypeWithHEAD,
	processImageUrl
} from '../EmbedHelpers';
import {
	ERROR_MESSAGES,
	SUPPORTED_IMAGE_FORMATS
} from '../types';
import type { EmbedImagesProps, ImageUrlData } from '../types';

export interface EmbedImagesRef {
	process: () => Promise<void>;
}

const EmbedImages = forwardRef<EmbedImagesRef, EmbedImagesProps>(({
	url,
	isProcessing,
	imagePreview,
	setImagePreview,
	onUploadComplete,
	onError,
	companyId,
	fromCreation
}, ref) =>
{
	/**
	 * Update preview when URL changes and it's a data URI
	 */
	useEffect(() =>
	{
		// Clear preview when URL is cleared
		if (!url)
		{
			setImagePreview(null);

			return;
		}

		// Set preview for image data URIs
		if (url.startsWith('data:image/'))
		{
			setImagePreview(url);
		}
		else
		{
			setImagePreview(null);
		}
	}, [ url, setImagePreview ]);

	/**
	 * Validates image URL
	 */
	const validateImageUrl = useCallback(async (inputUrl: string): Promise<boolean> =>
	{
		// Enhanced image URL validation to handle complex URLs better

		// 1. First check for Google image URLs (including encrypted thumbnails)
		const isGoogleImage = inputUrl.includes('googleusercontent.com') ||
			inputUrl.includes('encrypted-tbn') ||
			inputUrl.includes('google.com/images') ||
			(inputUrl.includes('google.com') && (
				inputUrl.includes('=pa-nu-') ||
				inputUrl.includes('=e365-') ||
				inputUrl.includes('&q=') ||
				inputUrl.includes('=w')
			)) ||
			inputUrl.includes('lh3.googleusercontent.com');

		// Always accept Google images
		if (isGoogleImage)
		{
			return true;
		}

		// 2. For other URLs, validate file extension or known patterns
		const isValid = validateFileTypeFromUrl(inputUrl, SUPPORTED_IMAGE_FORMATS);

		if (!isValid)
		{
			// 3. If standard validation fails, try HEAD request verification
			try
			{
				console.log('URL failed standard validation, attempting HEAD request verification');
				const verifiedType = await verifyImageTypeWithHEAD(inputUrl);

				if (verifiedType && SUPPORTED_IMAGE_FORMATS.includes(verifiedType === 'jpeg' ? 'jpg' : verifiedType))
				{
					console.log('HEAD request verification succeeded with type:', verifiedType);

					return true;
				}
				else
				{
					onError(ERROR_MESSAGES.INVALID_IMAGE_URL);

					return false;
				}
			}
			catch (e)
			{
				onError(ERROR_MESSAGES.INVALID_IMAGE_URL);

				return false;
			}
		}

		return true;
	}, [ onError ]);

	/**
	 * Handles the upload of an image URL directly to the API
	 */
	const handleImageUrlUpload = useCallback(async (
		imageData: {
			url: string;
			title?: string;
			description?: string;
			fileType: string;
			thumbnail?: string;
			platform: string;
			metadata?: any; // Include metadata for data URIs
		}
	) =>
	{
		try
		{
			// Check if we're handling a data URI
			const isDataUri = imageData.url.startsWith('data:image/');

			if (isDataUri)
			{
				console.log('Processing data URI upload to server');
			}

			// Add special handling for Google image URLs
			if ((imageData.url.includes('googleusercontent.com')) || (imageData.url.includes('google.com') && (imageData.url.includes('=pa-nu-') || imageData.url.includes('=e365-'))))
			{
				// Make sure we're treating it as a supported type
				if (![ 'jpg', 'jpeg', 'png', 'webp' ].includes(imageData.fileType))
				{
					console.log('Unsupported file type detected, defaulting to jpg');
					imageData.fileType = 'jpg'; // Default to jpg for unknown types
				}
			}

			// Create the request data payload for the API following the ImageUrlData interface
			const requestData: ImageUrlData = {
				url         : imageData.url,
				title       : imageData.title,
				description : imageData.description || '',
				fileType    : imageData.fileType,
				thumbnail   : imageData.thumbnail,
				platform    : imageData.platform || 'image_url',
				metadata    : imageData.metadata,
				requestFrom : 'embedLink'
			};

			// Use the imported uploadImageURL function directly
			const response = await uploadImageURL(companyId, requestData as ImageUrlData);

			return response;
		}
		catch (error)
		{
			// If there's a more specific error message in the error object, log it
			if (error instanceof Error)
			{
				console.error('Error details:', error.message);

				// If there's a response in the error, log it
				if ((error as any).response)
				{
					console.error('API response error:', (error as any).response);
				}
			}
			throw error;
		}
	}, [ companyId ]);

	/**
	 * Processes and uploads image
	 */
	const processAndUploadImage = useCallback(async (inputUrl: string): Promise<void> =>
	{
		try
		{
			// 1. Validate the image URL
			const isValid = await validateImageUrl(inputUrl);

			if (!isValid)
			{
				return;
			}

			// 2. Process Image URL (now async)
			let processedData;

			try
			{
				processedData = await processImageUrl(inputUrl);

				if (!processedData)
				{
					throw new Error('Failed to process Image URL');
				}
			}
			catch (imageError)
			{
				const errorMessage = imageError instanceof Error ? imageError.message : 'Failed to process image';

				onError(errorMessage);

				return;
			}

			// 3. Handle Image upload - Only call API if companyId is provided AND we're not just selecting for category/subcategory
			// For category/subcategory creation, we just need the URL, not to process it through the image API
			const shouldUploadToApi = companyId && fromCreation !== 'category';

			if (shouldUploadToApi)
			{
				const imageUploadParams = {
					url       : processedData.url,
					title     : processedData.title,
					fileType  : processedData.fileType || 'jpg',
					thumbnail : processedData.thumbnail,
					platform  : processedData.platform || 'image_url',
					// Pass the metadata for data URIs
					metadata  : processedData.metadata
				};

				console.log('Image upload parameters:', JSON.stringify(imageUploadParams, null, 2));

				const response = await handleImageUrlUpload(imageUploadParams);

				console.log('Image URL upload successful:', response);
			}
			else
			{
				console.log('Skipping image API call - this is for category/subcategory selection');
			}

			// 4. Notify parent of successful processing
			onUploadComplete(processedData);
		}
		catch (uploadError)
		{
			console.error('Error processing image URL:', uploadError);
			const errorMessage = uploadError instanceof Error
				? uploadError.message
				: 'Failed to process image URL. Please try again.';

			onError(errorMessage);
			toast.error('Failed to process image URL. Please try again.');
		}
	}, [ validateImageUrl, companyId, handleImageUrlUpload, onUploadComplete, onError ]);

	/**
	 * Main processing function called by parent
	 */
	const processImage = useCallback(async (): Promise<void> =>
	{
		if (isProcessing || !url.trim()) return;

		await processAndUploadImage(url);
	}, [ url, isProcessing, processAndUploadImage ]);

	// Expose the process function for parent to call
	useImperativeHandle(ref, () => ({
		process : processImage
	}));

	// This component doesn't render any UI, it's purely for logic
	// The parent component handles all UI rendering
	return null;
});

EmbedImages.displayName = 'EmbedImages';

export default EmbedImages;